import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, setToken, getToken } from '../lib/api'
import type { User } from '../../../shared/types'

interface LoginCredentials {
  email: string
  password: string
}

interface AuthResponse {
  user: User
  token: string
}

export function useAuth() {
  const queryClient = useQueryClient()
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const token = getToken()
      console.log('[Auth] Checking auth, token exists:', !!token)
      if (!token) {
        return null
      }
      try {
        const result = await apiGet<{ user: User }>('/auth/me')
        console.log('[Auth] Got user:', result?.user?.email)
        return result
      } catch (err: unknown) {
        console.error('[Auth] Failed to get user:', err)
        // Only clear token on 401 (unauthorized), not other errors
        const error = err as { status?: number }
        if (error.status === 401) {
          console.log('[Auth] 401 error, clearing token')
          setToken(null)
          return null
        }
        // For other errors, throw so React Query can handle retry logic
        throw err
      }
    },
    retry: (failureCount, error: unknown) => {
      // Don't retry on auth errors
      const apiError = error as { status?: number }
      if (apiError.status === 401 || apiError.status === 403) {
        return false
      }
      return failureCount < 2
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // Prevent refetch that could cause flicker
  })
  
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => 
      apiPost<AuthResponse>('/auth/login', credentials),
    onSuccess: (data) => {
      if (data.token) {
        setToken(data.token)
      }
      // Set the query data directly without invalidating to avoid refetch race
      queryClient.setQueryData(['auth', 'me'], { user: data.user })
    },
  })
  
  const logoutMutation = useMutation({
    mutationFn: () => apiPost('/auth/logout'),
    onSuccess: () => {
      setToken(null)
      queryClient.setQueryData(['auth', 'me'], null)
      queryClient.clear()
    },
  })
  
  return {
    user: data?.user ?? null,
    isLoading,
    error,
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    refetchAuth: refetch,
  }
}
